import React from "react";
import { useSurveyStatus } from "../../hooks/useSurveyStatus";
import { useNavigate } from "react-router-dom";
import { ProgressBar, Button, Typography } from "../../components/common";
import { Clock, Lock, CheckCircle } from "lucide-react";
import { MasterCourseLevelWithProgress, LevelStatus } from "../../store/api/masterCourseApi";
import SuggestionBoardImage from "../../images/SuggestionBoardImage";
import Tooltip from "@mui/material/Tooltip";
import { formatTimeLeft } from "../../utils/progressUtils";

interface LevelCardProps {
  level: MasterCourseLevelWithProgress;
  masterCourseId: string;
  isTrialOrg?: boolean;
}

const getStatusStyles = (status: LevelStatus) => {
  switch (status) {
  case "completed":
    return {
      border: "border-green-200 bg-green-50",
      overlay: "",
      cursor: "cursor-pointer",
    };
  case "in_progress":
    return {
      border: "border-primary/30 bg-white",
      overlay: "",
      cursor: "cursor-pointer",
    };
  case "not_started":
    return {
      border: "border-slate-300 bg-white",
      overlay: "",
      cursor: "cursor-pointer",
    };
  case "locked":
  case "locked_by_plan":
    return {
      border: "border-slate-200 bg-slate-50",
      overlay: "opacity-60",
      cursor: "cursor-not-allowed",
    };
  default:
    return {
      border: "border-slate-300 bg-white",
      overlay: "",
      cursor: "cursor-pointer",
    };
  }
};

export const LevelCard: React.FC<LevelCardProps> = ({
  level,
  masterCourseId,
  isTrialOrg = false,
}) => {
  const navigate = useNavigate();
  const { status: surveyStatus } = useSurveyStatus();
  const styles = getStatusStyles(level.status);
  const isPlanLocked = level.status === "locked_by_plan";
  const isLocked = level.status === "locked" || isPlanLocked;
  const isCompleted = level.status === "completed";

  const handleClick = () => {
    if (!isLocked) {
      // Navigate to Level detail page with master course ID in the route
      const returnTo = `/master-courses/${masterCourseId}/levels/${level.id}`;
      if (surveyStatus?.baselineRequired) {
        navigate("/survey/start", { state: { returnTo } });
      } else {
        navigate(returnTo);
      }
    }
  };

  const cardContent = (
    <div
      className={`relative rounded-2xl shadow-sm flex flex-col w-full h-full min-h-[280px] p-5 overflow-hidden border ${styles.border} ${styles.cursor} ${styles.overlay} transition-all hover:shadow-md`}
      onClick={handleClick}
    >
      {/* Completed badge */}
      {isCompleted && (
        <div className="absolute top-4 right-4 flex items-center gap-1 text-green-600 bg-green-100 px-2 py-1 rounded-full">
          <CheckCircle size={16} />
          <span className="text-sm font-medium">Complete</span>
        </div>
      )}

      {/* Lock icon for locked state */}
      {isLocked && (
        <div className="absolute top-4 right-4 flex items-center gap-1 text-slate-500 bg-slate-200 px-2 py-1 rounded-full">
          <Lock size={16} />
          <span className="text-sm font-medium">Locked</span>
        </div>
      )}

      {/* Level icon */}
      <div className="w-full h-[120px] flex justify-start items-center bg-transparent">
        <div className="relative w-[140px] h-[120px] overflow-hidden">
          {level.iconUri ? (
            <img
              src={level.iconUri}
              alt={level.title}
              className="absolute inset-0 w-full h-full object-cover rounded-lg"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <SuggestionBoardImage />
            </div>
          )}
        </div>
      </div>

      {/* Level info */}
      <div className="flex flex-col flex-1 w-full gap-2 mt-3">
        <Typography
          variant="small"
          weight="bold"
          color="none"
          className="text-textPrimary-grey leading-[120%] uppercase"
        >
          Module {level.levelNumber}
        </Typography>

        <Typography
          variant="h5"
          weight="bold"
          className="text-gray-900 line-clamp-2"
        >
          {level.title}
        </Typography>

        {/* Progress bar */}
        {!isLocked && (
          <div className="flex items-center gap-4 mt-1">
            <ProgressBar progress={level.progressPercentage} />
          </div>
        )}

        {/* Separator */}
        <div className="border-t border-gray-200 my-2"></div>

        {/* Actions and Info */}
        <div className="flex items-center justify-between gap-2">
          {!isLocked ? (
            <Button
              buttonType="button"
              label={surveyStatus?.baselineRequired ? "Take the baseline survey" : isCompleted ? "Review" : level.progressPercentage > 0 ? "Continue" : "Start"}
              variant="filled"
              className="flex-1 font-semibold px-4 py-2 bg-primary text-white hover:bg-primary/90 transition"
              onClick={handleClick}
            />
          ) : (
            <div className="flex-1 text-center text-sm text-slate-500 py-2">
              {isPlanLocked
                ? isTrialOrg
                  ? "Not included in your trial"
                  : "Not included in your plan"
                : "Complete previous level to unlock"}
            </div>
          )}
          <div className="flex items-center gap-1 text-gray-500 text-sm">
            <Clock size={16} strokeWidth={1} />
            {formatTimeLeft(
              level.progressPercentage,
              level.estimatedDurationMinutes,
              isCompleted
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Wrap locked cards with tooltip
  if (isLocked && level.unlockMessage) {
    return (
      <Tooltip title={level.unlockMessage} arrow placement="top">
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};

export default LevelCard;
